"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircle2, Info } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { SeedBackup } from "@/lib/bap/seed-contract";
import { checkSeedProfileRestore, prepareSeedProfileRestore, restoreSeedProfile, type SeedRestoreSuccess } from "@/lib/bap/seed-restore";
import { RecoveryPhraseInput } from "./recovery-phrase-input";
export interface RecoveryPhraseRestoreProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestored: (result: SeedRestoreSuccess) => void;
}
const PROFILE_NUMBER = /^\d+$/;
function RestoreFlow({
  onCancel,
  onRestored,
  operation
}: {
  onCancel: () => void;
  onRestored: (result: SeedRestoreSuccess) => void;
  operation: {
    current: AbortController | null;
  };
}) {
  const [profileNumber, setProfileNumber] = useState("0");
  const [candidate, setCandidate] = useState<SeedBackup | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => () => operation.current?.abort(), [operation]);
  function reset() {
    operation.current?.abort();
    operation.current = null;
    setCandidate(null);
    setPassword("");
    setConfirmation("");
    setPending(false);
    setError("");
  }
  function cancel() {
    reset();
    onCancel();
  }
  async function findAccount(words: string) {
    if (pending) {
      return;
    }
    let backup: SeedBackup;
    try {
      if (!PROFILE_NUMBER.test(profileNumber)) {
        throw new Error("Invalid profile");
      }
      backup = prepareSeedProfileRestore(words, Number(profileNumber));
    } catch {
      setError("Check the recovery phrase and selected profile.");
      return;
    }
    const controller = new AbortController();
    operation.current = controller;
    setPending(true);
    setError("");
    const result = await checkSeedProfileRestore(backup, controller.signal);
    if (controller.signal.aborted) {
      return;
    }
    setPending(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setCandidate(backup);
  }
  async function restore() {
    if (!candidate || pending) {
      return;
    }
    if (password.length < 8 || password !== confirmation) {
      setError("Use at least 8 characters and enter the same password twice.");
      return;
    }
    const controller_0 = new AbortController();
    operation.current = controller_0;
    setPending(true);
    setError("");
    const result_0 = await restoreSeedProfile({
      backup: candidate,
      password,
      signal: controller_0.signal
    });
    if (controller_0.signal.aborted) {
      return;
    }
    setPending(false);
    if (!result_0.ok) {
      setError(result_0.error);
      return;
    }
    reset();
    onRestored({
      bapId: result_0.bapId,
      ...(result_0.redirectURI ? {
        redirectURI: result_0.redirectURI
      } : {})
    });
  }
  return <>
			<DialogHeader className="pr-7 text-left">
				<div className="flex items-center gap-2">
					<DialogTitle>Use recovery phrase</DialogTitle>
					<Popover>
						<PopoverTrigger asChild>
							<Button aria-label="About phrase recovery" className="size-8 shrink-0 text-muted-foreground" size="icon" type="button" variant="ghost">
								<Info className="size-4" />
							</Button>
						</PopoverTrigger>
						<PopoverContent align="end" className="w-72 space-y-3 text-sm">
							<p>
								Your words stay on this device. Sigma checks a signed proof
								before saving a backup.
							</p>
							<p className="text-muted-foreground">
								A phrase recovers access to one profile. Import your full backup
								file to restore saved names and the complete profile list, and
								to enable cloud updates or additional profiles.
							</p>
							<p className="text-muted-foreground">
								Additional BIP-39 passphrases are not supported.
							</p>
							{candidate ? <p className="break-all font-mono text-xs">
									Profile {candidate.profiles[0].index}
									<br />
									{candidate.profiles[0].bapId}
								</p> : <details className="border-t pt-3">
									<summary className="cursor-pointer font-medium">
										Recover a different profile
									</summary>
									<div className="mt-3 space-y-2">
										<Label htmlFor="restore-profile-number">
											Profile number
										</Label>
										<Input autoComplete="off" disabled={pending} id="restore-profile-number" inputMode="numeric" onChange={event => {
                  setProfileNumber(event.target.value);
                  setError("");
                }} value={profileNumber} />
										<p className="text-muted-foreground text-xs">
											The first profile is 0. Change this only if you know the
											number from your backup. Every selection must pass the
											account check.
										</p>
									</div>
								</details>}
						</PopoverContent>
					</Popover>
				</div>
				<DialogDescription>
					{candidate ? "Protect your recovered backup on this device." : "Sign in to an existing Sigma account."}
				</DialogDescription>
			</DialogHeader>
			{candidate ? <form className="space-y-5" onSubmit={async event_0 => {
      event_0.preventDefault();
      await restore();
    }}>
					<div className="flex items-center gap-2 text-sm" role="status">
						<CheckCircle2 className="size-4 text-primary" />
						Account found
					</div>
					<div className="space-y-2">
						<Label htmlFor="restore-backup-password">Backup password</Label>
						<Input autoComplete="new-password" disabled={pending} id="restore-backup-password" minLength={8} onChange={event_1 => setPassword(event_1.target.value)} required type="password" value={password} />
						<p className="text-muted-foreground text-xs">
							At least 8 characters. This encrypts your backup on this device.
						</p>
					</div>
					<div className="space-y-2">
						<Label htmlFor="restore-backup-confirmation">
							Confirm password
						</Label>
						<Input autoComplete="new-password" disabled={pending} id="restore-backup-confirmation" onChange={event_2 => setConfirmation(event_2.target.value)} required type="password" value={confirmation} />
					</div>
					{error && <p className="text-destructive text-sm" role="alert">
							{error}
						</p>}
					<div className="flex gap-3">
						<Button aria-busy={pending} className="flex-1" disabled={pending || !password || !confirmation} type="submit">
							{pending ? "Signing in…" : "Sign in"}
						</Button>
						<Button onClick={cancel} type="button" variant="outline">
							Cancel
						</Button>
					</div>
					<Button className="h-auto p-0 text-muted-foreground" disabled={pending} onClick={reset} type="button" variant="link">
						Use a different phrase
					</Button>
				</form> : <RecoveryPhraseInput error={error} onCancel={cancel} onValidated={findAccount} pending={pending} />}
		</>;
}

/** Controlled dialog; mounting does not read storage, derive keys, or make requests. */
export function RecoveryPhraseRestore(t0) {
  const $ = _c(12);
  const {
    open,
    onOpenChange,
    onRestored
  } = t0;
  const operation = useRef(null);
  let t1;
  if ($[0] !== onOpenChange) {
    t1 = next => {
      if (!next) {
        operation.current?.abort();
      }
      onOpenChange(next);
    };
    $[0] = onOpenChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onOpenChange || $[3] !== onRestored || $[4] !== open) {
    t2 = open && <RestoreFlow onCancel={() => onOpenChange(false)} onRestored={result => {
      onOpenChange(false);
      onRestored(result);
    }} operation={operation} />;
    $[2] = onOpenChange;
    $[3] = onRestored;
    $[4] = open;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">{t2}</DialogContent>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== open || $[9] !== t1 || $[10] !== t3) {
    t4 = <Dialog onOpenChange={t1} open={open}>{t3}</Dialog>;
    $[8] = open;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}